import React from "react"

import { Button, ButtonVariant } from "../button/Button"
import { DeviceInfo } from "../device/DeviceInfo"
import { withDeviceInfo } from "../device/withDeviceInfo"
import { HotKeySet } from "../keyboard/HotKeyResponder"
import { Text } from "../text/Text"
import { CameraPopup } from "./CameraPopup"
import { PollFrameSource, RawFrameSource } from "./PollingSession"

interface InjectedProps {
  /**
   * Injected by withDeviceInfo() HoC wrapper.
   */
  deviceInfo: DeviceInfo | undefined
}

/**
 * Hot keys for the capture controls, named by what the worker is doing rather
 * than by which button is on screen, so the same key set applies whichever
 * capture mode resolves.
 *
 * `capture` drives both the trigger button and the viewfinder's Capture button:
 * one worker intent, two buttons that are never active at the same moment.
 *
 * The remaining keys belong to controls that only exist in some modes — the
 * preview phase has no counterpart under polling. Keys for absent controls are
 * simply inert.
 *
 * Deliberately no defaults: hot key conflicts are a property of the whole
 * screen, which a component cannot see. The view picks the keys.
 */
export interface CaptureHotKeys {
  /** Take the picture. */
  capture?: HotKeySet
  /** Accept the still preview. Single-shot only. */
  confirmPhoto?: HotKeySet
  /** Discard the still and return to the viewfinder. Single-shot only. */
  retakePhoto?: HotKeySet
  /** Abandon the camera. */
  cancel?: HotKeySet
  /** Key the value in by hand instead of waiting. Polling only. */
  manualEntry?: HotKeySet
}

export interface ImageCaptureProps {
  /**
   * Form field name.
   */
  name: string

  /**
   * Disable automatic form submission after image capture.
   *
   * The default behaviour is for the form to automatically submit after the image is captured.
   */
  disableAutoSubmit?: boolean

  /**
   * JPEG image compression quality factor.
   * Range: 0.0 (lowest quality, smallest size) to 1.0 (highest quality, largest size).
   * Applies only to JPEG output; ignored when `imageType` is `"image/png"` because
   * PNG is a lossless format that does not accept a quality parameter.
   * @default 0.7
   */
  imageQuality?: number

  /**
   * Output image format for the captured image.
   *
   * - `"image/jpeg"` (default): Lossy compression controlled by `imageQuality`.
   *   Good balance of quality and file size for most use cases.
   * - `"image/png"`: Lossless encoding that preserves sharp text edges.
   *   Recommended for OCR use cases where compression artifacts degrade
   *   text recognition accuracy. When using PNG, `imageQuality` has no effect
   *   (PNG does not accept a quality parameter). Use `maxWidth` and `maxHeight`
   *   to control output size and stay within the ~500 KB TTS request size limit.
   *
   * @default "image/jpeg"
   */
  imageType?: "image/jpeg" | "image/png"

  /**
   * Maximum width in pixels for the output image.
   * Image will be resized to fit within this width while preserving aspect ratio.
   * If only maxWidth is specified, height scales proportionally.
   */
  maxWidth?: number

  /**
   * Maximum height in pixels for the output image.
   * Image will be resized to fit within this height while preserving aspect ratio.
   * If only maxHeight is specified, width scales proportionally.
   */
  maxHeight?: number

  /**
   * Force capture through the in-app viewfinder on every device type.
   *
   * When false the surface follows the device: mobile opens a native file input
   * with `capture="environment"` for the platform camera app's better stills,
   * everything else uses the viewfinder. Required by `autoCapture` on mobile,
   * since a file picker cannot be polled.
   *
   * @default false
   */
  useInAppCamera?: boolean

  /**
   * Drive the camera popup in polling mode
   *
   * Requires `useInAppCamera`: polling reads frames from a live viewfinder,
   * which the native camera does not provide. Reported through
   * `onAutoCaptureUnavailable` when requested without it.
   *
   * @default false
   */
  autoCapture?: boolean

  /**
   * Maximum width in pixels for frames encoded during a polling session
   */
  pollMaxWidth?: number

  /**
   * Maximum height in pixels for frames encoded during a polling session
   */
  pollMaxHeight?: number

  /**
   * Hot key or set of hot keys that triggers the capture button.
   *
   * Superseded by `hotKeys.capture` when both are supplied.
   */
  hotKey?: HotKeySet

  /**
   * Hot keys for every capture control, including the ones inside the
   * viewfinder. See CaptureHotKeys.
   */
  hotKeys?: CaptureHotKeys

  /**
   * Button variant for the capture trigger button.
   * @default "primary"
   */
  buttonVariant?: ButtonVariant

  /**
   * Automatically open the camera when the component mounts.
   *
   * When `true`, the camera will open immediately after the component mounts
   * and camera availability is detected — without requiring the user to press
   * the capture button first.
   *
   * Only opens the in-app viewfinder, and only when detection found a camera.
   * A gesture-less click on the native input is blocked on managed handhelds,
   * so the worker has to press the trigger in that mode.
   *
   * @default false
   */
  autoOpenCamera?: boolean

  /**
   * Callback invoked with the processed base64 image data after capture.
   * Called with disableAutoSubmit=true to allow the parent to handle
   * the image data before any form submission occurs.
   *
   * Main purpose is for frontend OCR integration.
   */
  onCapture?: (base64Image: string) => void

  /**
   * Callback invoked with an encoded-frame source once the polling viewfinder is
   * live
   */
  onPollFrameSource?: (source: PollFrameSource | null) => void

  /**
   * Invoked when the worker asks to key the date in by hand instead of waiting
   * for the polling loop to read it.
   */
  onRequestManualEntry?: () => void

  onAutoCaptureUnavailable?: () => void

  /**
   * Invoked once per mount when no camera can be captured from, on every device
   * type. On the viewfinder there is no file-picker fallback, so a caller that
   * wants to take the worker off this screen must listen for this; left
   * unhandled, the overlay stays up showing the failure to retry or cancel.
   *
   * Unlike `onAutoCaptureUnavailable`, not gated on `autoCapture`.
   */
  onCameraUnavailable?: (reason: CameraUnavailableReason) => void

}

interface ImageCaptureState {
  /**
   * Stringified JSON of captured image data.
   * Stored as string for efficiency (avoids re-stringifying on every render).
   * Will be submitted to form as { type, data } object.
   */
  imageDataJson: string

  /**
   * Whether detection found a video input. Gates polling mode and nothing else:
   * the trigger opens the overlay either way, which reports its own failure.
   */
  cameraAvailable: boolean

  /**
   * Whether the CameraPopup is currently open.
   */
  popupOpen: boolean
}

/**
 * Output dimension constraints for a single encode.
 *
 * Exists so the encode path can be pointed at a different pair of dimensions
 * without forking the encoder: `ImageCaptureProps` is itself a valid
 * `OutputConstraints`, so the single-shot path passes nothing and picks up
 * `this.props`, while the polling path passes the resolved poll dimensions.
 * Internal — not part of the public API.
 */
interface OutputConstraints {
  maxWidth?: number
  maxHeight?: number
}

/**
 * What camera detection concluded. Internal — not part of the public API.
 */
type CaptureModeResolution = "camera" | "noCamera"

/**
 * Why no camera is available to capture from.
 *
 * Enumeration only reports whether a device exists; acquisition is what proves
 * it can be used. A camera the browser refuses to open — permission withheld,
 * another application holding it, blocked by document policy — is as unusable
 * as an absent one, but it is a different thing to record.
 *
 * Values match the audit contract's SkipReason.
 */
export type CameraUnavailableReason = "NO_HARDWARE" | "FAULTY_HARDWARE"

/**
 * Image capture component for capturing images from device camera.
 *
 * Features:
 * - Primary button trigger instead of default file input UI
 * - Optional aspect-ratio-preserving resize with maxWidth/maxHeight
 * - Configurable output format via `imageType` (JPEG or PNG)
 * - Compresses images to JPEG with configurable quality (PNG is lossless)
 * - Auto-submits form by default (can be disabled)
 *
 * Resize behavior:
 * - Preserves aspect ratio - image never distorts
 * - Fits within both maxWidth and maxHeight constraints
 * - Never upscales - only downscales if image exceeds constraints
 *
 * For 500 KB TTS limit, recommended settings:
 * - maxWidth: 1280, maxHeight: 720, imageQuality: 0.7
 * - Expected output: 250-400 KB base64
 */
export class ImageCaptureBase extends React.PureComponent<
  ImageCaptureProps & InjectedProps,
  ImageCaptureState
> {

  public static defaultProps = {
    imageType: "image/jpeg" as const,
    imageQuality: 0.7,
    buttonVariant: "primary" as const,
    autoOpenCamera: false,
    autoCapture: false,
    useInAppCamera: false,
  }

  private readonly imageDataInputRef = React.createRef<HTMLInputElement>()

  private readonly fileInputRef = React.createRef<HTMLInputElement>()

  // Guards async work in detectCameraAvailability from touching state after
  // unmount (e.g. when DateCapture remounts this via key={retryCount}).
  private mounted = false

  // Guards onAutoCaptureUnavailable to a single call per mount: detection and a
  // later camera acquisition failure can both conclude no live frames.
  private autoCaptureUnavailableReported = false

  // Same guard for onCameraUnavailable: detection and a failed acquisition can
  // both conclude no camera, and a retake can fail more than once.
  private cameraUnavailableReported = false

  public constructor(props: ImageCaptureProps & InjectedProps) {
    super(props)
    this.state = {
      imageDataJson: "",
      cameraAvailable: false,
      popupOpen: false,
    }
  }

  public componentDidMount(): void {
    this.mounted = true
    this.detectCameraAvailability()
  }

  public componentWillUnmount(): void {
    this.mounted = false
  }

  /**
   * Detect camera availability, then report what detection concluded once from
   * the single point where it has finished.
   *
   * Detection is not the last word on availability — it only sees whether a
   * device exists. `handlePopupError` reports the camera that exists but will
   * not open.
   */
  private async detectCameraAvailability(): Promise<void> {
    const detected = await this.resolveCaptureMode()
    if (!this.mounted) {
      return
    }
    if (detected === "noCamera") {
      this.reportAutoCaptureUnavailable()
      this.reportCameraUnavailable("NO_HARDWARE")
      return
    }
    // A camera exists, but the native input cannot be polled. Report it rather
    // than leaving the caller waiting on a frame source that never arrives.
    if (!this.usesInAppCamera()) {
      this.reportAutoCaptureUnavailable()
    }
  }

  /**
   * Report at most once per mount that there is no camera to capture from.
   */
  private reportCameraUnavailable(reason: CameraUnavailableReason): void {
    if (this.cameraUnavailableReported) {
      return
    }
    this.cameraUnavailableReported = true
    this.props.onCameraUnavailable?.(reason)
  }

  /**
   * Resolve whether there is a camera, then honour `autoOpenCamera`. Runs on
   * every device type: the native input path still needs to know whether a
   * camera exists so `onCameraUnavailable` can fire.
   *
   * @returns "camera" when a video input was found, and "noCamera" when
   *   detection ran and turned up nothing usable — either no video input, or an
   *   API that could not be queried at all.
   */
  private async resolveCaptureMode(): Promise<CaptureModeResolution> {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return "noCamera"
      }
      const devices = await navigator.mediaDevices.enumerateDevices()
      if (!this.mounted) {
        return "noCamera"
      }
      const hasVideoInput = devices.some((d) => d.kind === "videoinput")
      if (hasVideoInput) {
        this.setState({ cameraAvailable: true }, () => {
          if (!this.mounted) {
            return
          }
          if (this.props.autoOpenCamera && this.usesInAppCamera()) {
            this.setState({ popupOpen: true })
          }
        })
        return "camera"
      }
      return "noCamera"
    } catch {
      // Detection failed; treat it as no camera.
      return "noCamera"
    }
  }

  /**
   * Report at most once per mount that `autoCapture` cannot be honoured, so a
   * caller can record that automatic capture was requested and unavailable
   * rather than inferring it from a frame source that never arrives.
   *
   * A no-op when `autoCapture` was never requested.
   */
  private reportAutoCaptureUnavailable(): void {
    if (this.autoCaptureUnavailableReported || !this.props.autoCapture) {
      return
    }
    this.autoCaptureUnavailableReported = true
    this.props.onAutoCaptureUnavailable?.()
  }

  /**
   * Handle confirmed image from CameraPopup.
   * Processes the captured data URL through the existing image pipeline,
   * stores the result as JSON in the hidden input, closes the popup,
   * and auto-submits if enabled.
   */
  private handlePopupConfirm = async (dataUrl: string) => {
    const { imageType, imageQuality, onCapture } = this.props
    this.setState({ popupOpen: false })

    const processedBase64 = await this.processImage(dataUrl, imageType!, imageQuality!)
    const imageDataJson = JSON.stringify({
      type: imageType,
      data: processedBase64
    })

    this.setState({ imageDataJson }, () => {
      if (onCapture) {
        onCapture(processedBase64)
      }
      if (!this.props.disableAutoSubmit) {
        this.submitForm()
      }
    })
  }

  /**
   * Handle user-initiated cancel from CameraPopup.
   * Closes the popup without any fallback, and ends any active polling session —
   * the viewfinder the session pulls frames from is going away.
   */
  private handlePopupCancel = () => {
    this.setState({ popupOpen: false })
    this.props.onPollFrameSource?.(null)
  }

  /**
   * The worker asked to type the date instead. Report the request upward first,
   * then close the viewfinder and end any session.
   *
   * The order matters and is the opposite of `handlePopupCancel`: deregistering
   * the frame source is what ends the consumer's polling session, and the
   * consumer needs to be asked while that session still exists so it can record
   * how many requests the worker waited through. Ending it first silently loses
   * that count.
   */
  private handleRequestManualEntry = () => {
    this.props.onRequestManualEntry?.()
    this.setState({ popupOpen: false })
    this.props.onPollFrameSource?.(null)
  }

  /**
   * Handle camera acquisition error from CameraPopup.
   *
   * Reports FAULTY_HARDWARE rather than NO_HARDWARE: enumeration found a device,
   * so the hardware exists and the browser refused to open it.
   *
   * Leaves the overlay up. There is no file picker in this mode, so closing it
   * would leave a bare trigger with no explanation; a caller that requires a
   * camera acts on `onCameraUnavailable` instead.
   */
  private handlePopupError = () => {
    this.props.onPollFrameSource?.(null)
    this.reportAutoCaptureUnavailable()
    this.reportCameraUnavailable("FAULTY_HARDWARE")
  }

  /**
   * Device type decides the surface, not camera presence: a handheld and a
   * laptop both have cameras, but only the handheld has a camera app worth
   * deferring to. An unknown type gets the viewfinder.
   */
  private usesInAppCamera(): boolean {
    if (this.props.useInAppCamera) {
      return true
    }
    return this.props.deviceInfo?.deviceType !== "mobile"
  }

  /**
   * Whether the camera popup should run in polling mode. Requires the caller's
   * opt-in, the in-app viewfinder, and a camera to pull live frames from.
   */
  private isPollingMode(): boolean {
    return !!this.props.autoCapture
      && this.usesInAppCamera()
      && this.state.cameraAvailable
  }

  /**
   * Wrap the raw frame puller handed up by CameraPopup in the encode path and
   * pass it on, so the encode stays inside this component and the caller only
   * ever sees processed base64.
   *
   * `null` is forwarded straight through: it means the viewfinder is gone
   * (cancel, camera error, unmount) and any session must end.
   */
  private handleFrameSourceReady = (raw: RawFrameSource | null) => {
    const { onPollFrameSource, imageType, imageQuality } = this.props
    if (!onPollFrameSource) {
      return
    }
    if (!raw) {
      onPollFrameSource(null)
      return
    }
    onPollFrameSource({
      grabEncodedFrame: async () => {
        const dataUrl = raw.grabFrame()
        if (!dataUrl) {
          return null
        }
        return this.processImage(
          dataUrl, imageType!, imageQuality!, this.resolvePollConstraints(),
        )
      },
      close: () => this.setState({ popupOpen: false }),
    })
  }

  /**
   * Resolve the capture width requested from the camera. Anchors the request to
   * the desired output width (`maxWidth`) so the stream can come up to that
   * resolution instead of the browser's 640x480 default. Returns undefined when
   * `maxWidth` is not set, leaving the stream resolution unconstrained as before.
   * Requested via an `ideal` constraint, so the camera degrades gracefully when
   * it cannot match.
   */
  private getRequestedCaptureWidth = (): number | undefined => {
    return this.props.maxWidth
  }

  /**
   * Resolve the capture height requested from the camera. See
   * `getRequestedCaptureWidth` for behavior.
   */
  private getRequestedCaptureHeight = (): number | undefined => {
    return this.props.maxHeight
  }

  private handleButtonClick = () => {
    if (this.usesInAppCamera()) {
      this.setState({ popupOpen: true })
    } else {
      this.fileInputRef.current?.click()
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target?.result as string)
      reader.onerror = () => resolve("")
      reader.readAsDataURL(file)
    })
  }

  /**
   * Resolve the dimension constraints for a polled frame. Each poll dimension
   * falls back to its single-shot counterpart, so a caller that only wants
   * smaller polled frames in one axis does not have to restate the other.
   */
  private resolvePollConstraints(): OutputConstraints {
    const { maxWidth, maxHeight, pollMaxWidth, pollMaxHeight } = this.props
    return {
      maxWidth: pollMaxWidth ?? maxWidth,
      maxHeight: pollMaxHeight ?? maxHeight,
    }
  }

  /**
   * Calculate output dimensions for a given image size, respecting maxWidth/maxHeight
   * constraints while preserving aspect ratio and never upscaling.
   *
   * Constraints are a parameter rather than read straight off `this.props` so a
   * polled frame can be sized independently. It defaults to `this.props`, which
   * is exactly the previous behaviour for every existing caller.
   */
  private calculateOutputDimensions(
    originalWidth: number,
    originalHeight: number,
    constraints: OutputConstraints = this.props
  ): { width: number; height: number } {
    const { maxWidth, maxHeight } = constraints

    if (!maxWidth && !maxHeight) {
      return { width: originalWidth, height: originalHeight }
    }

    // Calculate scale factors for each constraint
    const widthScale = maxWidth ? maxWidth / originalWidth : Infinity
    const heightScale = maxHeight ? maxHeight / originalHeight : Infinity

    // Use the smaller scale factor to ensure image fits within both constraints
    const scale = Math.min(widthScale, heightScale, 1) // Don't upscale

    return {
      width: Math.round(originalWidth * scale),
      height: Math.round(originalHeight * scale)
    }
  }

  /**
   * Process image by resizing (if specified) and compressing to the specified format.
   * @param dataUrl - Image data URL from FileReader
   * @param imageType - Output MIME type ("image/jpeg" or "image/png")
   * @param imageQuality - JPEG compression quality (ignored for PNG)
   * @param constraints - Output dimension constraints, defaulting to `this.props`.
   *   Polled and single-shot frames deliberately go through this one encoder so
   *   the bytes a poll evaluates and the bytes a confirmed capture emits are
   *   produced by identical logic; only the dimensions may differ.
   * @returns Promise resolving to the processed base64 data string
   */
  private processImage(
    dataUrl: string,
    imageType: "image/jpeg" | "image/png",
    imageQuality: number,
    constraints: OutputConstraints = this.props,
  ): Promise<string> {

    return new Promise((resolve) => {
      const img = new Image()

      img.onload = () => {
        const { width: outputWidth, height: outputHeight } =
          this.calculateOutputDimensions(img.width, img.height, constraints)

        // Create canvas with output dimensions
        const canvas = document.createElement("canvas")
        canvas.width = outputWidth
        canvas.height = outputHeight

        const ctx = canvas.getContext("2d")
        if (!ctx) {
          // Fallback: return original base64 if canvas context fails
          resolve(dataUrl.split(",")[1])
          return
        }

        // Draw resized image
        ctx.drawImage(img, 0, 0, outputWidth, outputHeight)

        // PNG is lossless — quality parameter is only meaningful for JPEG
        let processedDataUrl: string
        switch (imageType) {
          case "image/png":
            processedDataUrl = canvas.toDataURL("image/png")
            break
          case "image/jpeg":
            processedDataUrl = canvas.toDataURL("image/jpeg", imageQuality)
            break
          default: {
            // Errors at compile if new value added to imageType but not handled in this switch
            const exhaustiveCheck: never = imageType
            throw new Error(
              `Unsupported imageType: ${exhaustiveCheck}`,
            )
          }
        }

        resolve(processedDataUrl.split(",")[1])
      }

      img.onerror = () => {
        // Fallback: return original base64 if image loading fails
        resolve(dataUrl.split(",")[1])
      }

      img.src = dataUrl
    })
  }

  private handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { imageType, imageQuality, onCapture } = this.props
    const files = event.target.files
    const fileInput = event.target

    if (files && files[0]) {
      const file = files[0]

      const dataUrl = await this.readFileAsDataUrl(file)
      const processedBase64 = await this.processImage(dataUrl, imageType!, imageQuality!)

      // Stringify immediately to avoid stringifying on every render
      const imageDataJson = JSON.stringify({
        type: imageType,
        data: processedBase64
      })

      // Clear file input to prevent CORS/403 errors on form submission
      // File input's value must be cleared because browsers apply strict security
      // policies when forms with file inputs are submitted programmatically
      fileInput.value = ""

      this.setState({ imageDataJson }, () => {
        if (onCapture) {
          onCapture(processedBase64)
        }
        // If necessary, auto-submit after image is selected
        if (!this.props.disableAutoSubmit) {
          this.submitForm()
        }
      })
    }
  }

  private submitForm = () => {
    const formElement = this.imageDataInputRef.current?.form
    if (formElement) {
      formElement.requestSubmit()
    }
  }

  public render() {
      const {
        name,
        children = <Text>Capture Image</Text>,
        hotKey,
        hotKeys,
        buttonVariant,
      } = this.props
      const { imageDataJson, popupOpen } = this.state
      const inAppCamera = this.usesInAppCamera()

      // The trigger stays mounted behind the fullscreen popup, so leaving its
      // hot key bound would put two responders on one key while the viewfinder
      // is up — which is the point of letting `capture` drive both buttons.
      const triggerHotKey = popupOpen ? undefined : (hotKeys?.capture ?? hotKey)

      return (
        <div className="image-capture">
          {!inAppCamera && (
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={this.handleChange}
            />
          )}

          {popupOpen && (
            <CameraPopup
              requestedWidth={this.getRequestedCaptureWidth()}
              requestedHeight={this.getRequestedCaptureHeight()}
              pollingMode={this.isPollingMode()}
              onFrameSourceReady={this.handleFrameSourceReady}
              onRequestManualEntry={
                this.props.onRequestManualEntry
                  ? this.handleRequestManualEntry
                  : undefined
              }
              captureHotKey={hotKeys?.capture}
              confirmHotKey={hotKeys?.confirmPhoto}
              retakeHotKey={hotKeys?.retakePhoto}
              cancelHotKey={hotKeys?.cancel}
              manualEntryHotKey={hotKeys?.manualEntry}
              onConfirm={this.handlePopupConfirm}
              onCancel={this.handlePopupCancel}
              onError={this.handlePopupError}
            />
          )}

          <Button
            variant={buttonVariant}
            fluid={true}
            hotKey={triggerHotKey}
            onClick={this.handleButtonClick}
          >
            {children}
          </Button>

          <input
            ref={this.imageDataInputRef}
            type="hidden"
            name={`${name}$json`}
            value={imageDataJson}
          />
        </div>
      )
    }

}

export const ImageCapture = Object.assign(
  withDeviceInfo(ImageCaptureBase),
  {
    displayName: "ImageCapture",
  },
)
